import { useState } from 'react';
import { Modal, Table, Input, Label, Popup, Icon } from 'semantic-ui-react';

// Color convention matches the main report's own PracticeRow.js coloring (over base = green,
// base = red). The values themselves come from buildBaseAuditRows()/computePracticeStatus()
// — the same formula that decides the provider's own Final Status, applied per practice —
// not the backend's row.data[practice].isPracticeOverBase field.
const STATUS_COLORS = {
    'Base': 'red',
    'Over Base': 'green',
    'Not Tracked': 'grey',
    'No Base Configured': 'grey',
    'No Worked Days': 'grey',
};

const StatusLabel = ({ status }) => (
    <Label size="small" color={STATUS_COLORS[status] || 'grey'}>{status}</Label>
);

const practiceStatusLabel = (isPracticeOverBase) => {
    if (isPracticeOverBase === true) return 'Over Base';
    if (isPracticeOverBase === false) return 'Base';
    return 'N/A';
};

const PracticesCell = ({ practices = [] }) => {
    if (practices.length === 0) return <span style={{ color: 'rgba(0,0,0,.4)' }}>-</span>;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {practices.map(p => (
                <Label key={p.name} size="tiny" color={STATUS_COLORS[practiceStatusLabel(p.isPracticeOverBase)] || 'grey'} basic>
                    {p.name}
                    <Label.Detail>{practiceStatusLabel(p.isPracticeOverBase)}</Label.Detail>
                </Label>
            ))}
        </div>
    );
};

/**
 * BaseStatusModal
 *
 * Audit view behind the "Base Pay X%" badge on the By Dentist report. One row per provider,
 * showing the raw child-level Base/Over Base classification and the final status actually
 * counted by the badge (both from getProviderBaseAuditInfo(), the same function the badge
 * itself uses), plus the provider's practices with each practice's own isPracticeOverBase
 * result — so this table can never disagree with the number it explains.
 *
 * Props:
 *   open  - boolean
 *   onClose - () => void
 *   rows  - Array<{ abbr, doctorName, contractType, group, childStatus, finalStatus, practices }>
 */
const BaseStatusModal = ({ open, onClose, rows = [] }) => {
    const [search, setSearch] = useState('');

    const filteredRows = search
        ? rows.filter(row => {
            const q = search.toLowerCase();
            return (row.doctorName || '').toLowerCase().includes(q) ||
                (row.abbr || '').toLowerCase().includes(q) ||
                (row.contractType || '').toLowerCase().includes(q) ||
                (row.practices || []).some(p => (p.name || '').toLowerCase().includes(q));
        })
        : rows;

    // Matches the live badge exactly (rawFinalStatus is untouched by the "Other Practice
    // Making Base" display override below) — this summary should never disagree with the
    // "Base Pay X%" badge on the page.
    const baseCount = rows.filter(r => r.rawFinalStatus === 'Base').length;

    return (
        <Modal open={open} onClose={onClose} size="large" closeIcon>
            <Modal.Header>
                Base Pay Breakdown
                <span style={{ fontWeight: 400, marginLeft: 10, color: 'rgba(0,0,0,.6)' }}>
                    {baseCount}/{rows.length} providers on base
                </span>
            </Modal.Header>
            <Modal.Content>
                <Input
                    icon="search"
                    placeholder="Filter by provider, ABBR, practice, or contract type..."
                    value={search}
                    onChange={(e, { value }) => setSearch(value)}
                    style={{ marginBottom: 12 }}
                    fluid
                />
            </Modal.Content>
            <Modal.Content scrolling style={{ maxHeight: '60vh' }}>
                <Table celled striped compact>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Provider</Table.HeaderCell>
                            <Table.HeaderCell>Contract Type</Table.HeaderCell>
                            <Table.HeaderCell>Classification</Table.HeaderCell>
                            <Table.HeaderCell>Final Status (per counter)</Table.HeaderCell>
                            <Table.HeaderCell>Practices</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {filteredRows.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan="5" textAlign="center">No providers match this filter</Table.Cell>
                            </Table.Row>
                        )}
                        {filteredRows.map(row => (
                            <Table.Row key={row.abbr}>
                                <Table.Cell>{row.doctorName}</Table.Cell>
                                <Table.Cell>{row.contractType}</Table.Cell>
                                <Table.Cell>
                                    {row.group}{row.group !== 'Not Tracked' && <>: <StatusLabel status={row.childStatus} /></>}
                                </Table.Cell>
                                <Table.Cell>
                                    <StatusLabel status={row.finalStatus} />
                                    {row.finalStatusForced && (
                                        <Popup
                                            trigger={<Icon name="info circle" color="grey" style={{ marginLeft: 4, cursor: 'help' }} />}
                                            content="Forced Over Base: this provider works more than one practice, and at least one is over base while at least one is not (the Other Practice Making Base rule). The live Base Pay counter does not currently apply this — it still counts this provider as Base."
                                            position="top center"
                                        />
                                    )}
                                </Table.Cell>
                                <Table.Cell><PracticesCell practices={row.practices} /></Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </Modal.Content>
        </Modal>
    );
};

export default BaseStatusModal;
